import APP_CONFIG from '../../app-config/app-config';
import { Platform } from 'ionic-angular';
import { GoogleAnalytics } from '@ionic-native/google-analytics';
import { Injectable } from '@angular/core';

declare var gtag: Function;

@Injectable()
export class AnalyticsService {
	private started: boolean = false;
	private activeDimensions = {};

	private customMapNames = {
		'dimension1': 'Device Type',
		'dimension2': 'Medication Calculator: Age/Weight',
		'dimension3': 'Medication Calculator: Drug',
		'dimension4': 'Medication Calculator: Indication',
		'dimension5': 'Medication Calculator: Route',
		'dimension6': 'Infusion Calculator: Age/Weight',
		'dimension7': 'Infusion Calculator: Drug',
		'dimension8': 'Infusion Calculator: Indication',
		'dimension9': 'Infusion Calculator: Rate',
		'dimension10': 'Palliative Care Tool: Output',
		'dimension11': 'Burns Fluid Calculator: Age',
		'dimension12': 'Burns Fluid Calculator: Weight',
		'dimension13': 'Burns Fluid Calculator: Time',
		'dimension14': 'Burns Fluid Calculator: TBSA',
		'dimension15': 'Page Per Age Calculator: Age/Weight',

		'metric1': 'Medication Calculator: Result',
		'metric2': 'Infusion Calculator: Result',
		'metric3': 'Palliative Care Tool: Result',
		'metric4': 'Burns Fluid Calculator: Result',
		'metric5': 'Page Per Age Calculator: Result',
	};

  constructor(
		private googleAnalytics: GoogleAnalytics,
		private platform: Platform
	) {}

  startGoogleAnalytics(): void {
    if (APP_CONFIG.google_analytics_tracking_enabled) {
      if(this.platform.is('cordova')) {
				this.startGoogleAnalyticsForApp();
			}else{
				this.startGoogleAnalyticsForDesktop();
			}
    }
	}

	private startGoogleAnalyticsForApp(): void {
		if (this.platform.ready() && this.platform.is('cordova')) {
			this.googleAnalytics
				.startTrackerWithId(APP_CONFIG.google_analytics_tracking_id)
				.then(() => {
					// console.log('Google analytics is ready now');
					
					this.analyticsHasStarted();
				})
				.catch((e) => {
					// console.log('Error starting GoogleAnalytics', e)
				});
		}
	}

	private startGoogleAnalyticsForDesktop(): void {
		gtag('js', new Date());

		gtag('config', APP_CONFIG.google_analytics_tracking_id, {
			'send_page_view': false,
			'app_name': 'AV CPG',
			'app_version': APP_CONFIG.app_version,
			'custom_map': this.customMapNames
		});

		this.analyticsHasStarted();
	}

	private analyticsHasStarted(): void {
		this.started = true;

		//custom dimension

		if(this.platform.is('cordova')) {
			this.activeDimensions[this.customMapNames['dimension1']] = APP_CONFIG.google_analytics_tracking_dimension;
			this.addCustomDimension(1, APP_CONFIG.google_analytics_tracking_dimension);
		}else{
			this.activeDimensions[this.customMapNames['dimension1']] = 'Web';
			this.addCustomDimension(1, 'Web');
		}

		//app version
		this.setAppVersion(APP_CONFIG.app_version);

		// track launch event
		this.trackEvent('App Event', 'launch', 'App Launch');
	}
	
	private isAnalyticsActive(): boolean {
		if(APP_CONFIG.google_analytics_tracking_enabled && this.started) {
			return true;
		}

		return false;
	}

	private params(object1: any, object2: any = {}): any {
		return Object.assign({}, this.activeDimensions, object1, object2);
	}

  trackPage(page: string): void {
		if(!this.isAnalyticsActive()) { return; }

		// console.log('page', page);

		if(this.platform.is('cordova')) {
			this.googleAnalytics.trackView(page);
		}else{
			gtag('event', 'screen_view', this.params({
				'screen_name': page
			}));
		}
  }

  trackEvent(category: string, action: string, label?: string): void {
		if(!this.isAnalyticsActive()) { return; }

		// console.log('event', category, action, label);

		if(this.platform.is('cordova')) {
			this.googleAnalytics.trackEvent(category, action, label);
		}else{
			gtag('event', action, this.params({
				'event_category': category,
				'event_label': label
			}));
    }
	}
	
	metricDimensionHit(category: string, action: string, dimensions: any, metric: any): void {
		if(!this.isAnalyticsActive()) { return; }

		if(this.platform.is('cordova')) {
			for(let i = 0; i < dimensions.length; i++) {
				this.addCustomDimension(dimensions[i][0], dimensions[i][1]);
			}

			this.trackMetric(metric[0], metric[1]);
			this.trackEvent(category, action, this.customMapNames[`metric${metric[0]}`]);
		}else{
			let params = {};

			for(let i = 0; i < dimensions.length; i++) {
				// console.log('dimension', dimensions[i][0], dimensions[i][1]);
				params[this.customMapNames[`dimension${dimensions[i][0]}`]] = dimensions[i][1];
			}

			let metricName = this.customMapNames[`metric${metric[0]}`];
			params[metricName] = metric[1];

			// console.log('metric', metric[0], metric[1]);

			gtag('event', action, this.params(params, {
				'event_category': category,
				'event_label': metricName
			}));
		}
	}

  private trackMetric(key: number, value: number): void {
		if(!this.isAnalyticsActive()) { return; }

		// console.log('metric', key, value);

		if(this.platform.is('cordova')) {
			this.googleAnalytics.trackMetric(key, value);
		}
  }

  private addCustomDimension(key: number, value: string): void {
		if(!this.isAnalyticsActive()) { return; }

		// console.log('dimension', key, value);

		if(this.platform.is('cordova')) {
			this.googleAnalytics.addCustomDimension(key, value);
		}
  }

  setAppVersion(version: string): void {
		if(!this.isAnalyticsActive()) { return; }

		// console.log('version', version);

		if(this.platform.is('cordova')) {
			this.googleAnalytics.setAppVersion(version);
		}
	}
}



// WEBPACK FOOTER //
// ./src/services/analytics.service.ts